// Direct Usage Dashboard - Premium Midnight Luxe Design
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useAuth } from '@/providers/AuthProvider';
import {
  FileText,
  Upload,
  Clock,
  CheckCircle,
  TrendingUp,
  ArrowRight,
  Sparkles,
  Calendar,
  BarChart3,
  Zap,
  Code2,
  ArrowLeftRight,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { formatDate, cn } from '@/lib/utils';
import { StatCard } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PoweredByTao } from '@/components/ui/tao-logo';
import { toast } from '@/hooks/use-toast';

export const Route = createFileRoute('/dashboard')({
  component: Dashboard,
});

function Dashboard() {
  const { user, isReady } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: analyses, isLoading: analysesLoading } = useQuery({
    queryKey: ['analyses'],
    queryFn: () => api.analyses.list(),
    enabled: isReady,
  });

  const { data: jobs } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => api.jobs.list(),
    enabled: isReady,
  });

  const switchModeMutation = useMutation({
    mutationFn: () => api.users.update({ preferredMode: 'api' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      toast({ title: 'Switched to API mode' });
      navigate({ to: '/api-home' });
    },
  });

  const recentAnalyses = analyses?.data?.slice(0, 5) || [];
  const activeJobs = jobs?.data?.filter((j: any) => ['pending', 'active'].includes(j.status)) || [];

  const stats = {
    total: analyses?.total || 0,
    completed: analyses?.data?.filter((a: any) => a.status === 'complete' || a.status === 'completed').length || 0,
    inProgress: analyses?.data?.filter((a: any) => ['uploading', 'processing', 'financial_review', 'metrics_review', 'processing_remaining', 'generating', 'in_progress'].includes(a.status)).length || 0,
    draft: analyses?.data?.filter((a: any) => a.status === 'idle' || a.status === 'draft').length || 0,
  };

  return (
    <div className="min-h-screen bg-background gradient-mesh noise">
      {/* Header with gradient accent */}
      <div className="relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold-500/5 rounded-full blur-3xl" />
        <div className="absolute top-0 right-1/4 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl" />

        <div className="relative px-8 pt-10 pb-8">
          {/* Welcome Section */}
          <div className="flex items-start justify-between mb-10">
            <div className="animate-slideUp">
              <div className="flex items-center gap-3 mb-2">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-500/10 border border-gold-500/20">
                  <Sparkles className="w-3.5 h-3.5 text-gold-400" />
                  <span className="text-xs font-medium text-gold-400 uppercase tracking-wider">
                    Dashboard
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {new Date().toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>
              <h1 className="text-4xl font-display font-bold text-foreground mb-2">
                Welcome back, <span className="gradient-text">{user?.name?.split(' ')[0] || 'User'}</span>
              </h1>
              <p className="text-muted-foreground text-lg">
                Here's an overview of your credit analysis activity
              </p>
            </div>

            <div className="flex items-center gap-3 animate-slideUp" style={{ animationDelay: '0.1s' }}>
              {/* Mode Toggle */}
              <button
                onClick={() => switchModeMutation.mutate()}
                disabled={switchModeMutation.isPending}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-emerald-500/40 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-500/60 hover:text-emerald-300 transition-all text-sm font-medium disabled:opacity-50 shadow-lg shadow-emerald-500/5"
                title="Switch to API mode"
              >
                <ArrowLeftRight className="w-4 h-4" />
                <Code2 className="w-4 h-4" />
                API Mode
              </button>

              <Link to="/workflow">
                <Button variant="gold" size="lg" className="gap-2">
                  <Zap className="w-5 h-5" />
                  New Analysis
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
            <StatCard
              title="Total Analyses"
              value={stats.total}
              icon={<BarChart3 className="w-5 h-5" />}
              variant="gold"
            />
            <StatCard
              title="Completed"
              value={stats.completed}
              icon={<CheckCircle className="w-5 h-5" />}
              variant="emerald"
            />
            <StatCard
              title="In Progress"
              value={stats.inProgress}
              icon={<Clock className="w-5 h-5" />}
              variant="warning"
            />
            <StatCard
              title="Drafts"
              value={stats.draft}
              icon={<FileText className="w-5 h-5" />}
              variant="muted"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-8 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <div className="lg:col-span-1 animate-slideUp" style={{ animationDelay: '0.2s' }}>
            <div className="flex items-center gap-2 mb-6">
              <h2 className="text-xl font-display font-semibold text-foreground">Quick Actions</h2>
            </div>
            <div className="space-y-4">
              <QuickActionCard
                title="New Analysis"
                description="Start a new credit analysis"
                icon={<Upload className="w-5 h-5" />}
                to="/workflow"
                isPrimary
              />
              <QuickActionCard
                title="View All Analyses"
                description="Browse your analysis history"
                icon={<FileText className="w-5 h-5" />}
                to="/analyses"
              />
              <QuickActionCard
                title="Manage Templates"
                description="Create and edit templates"
                icon={<TrendingUp className="w-5 h-5" />}
                to="/templates"
              />
            </div>

            {/* Processing Jobs */}
            {activeJobs.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-gold-400 animate-pulse" />
                  <h3 className="text-sm font-medium text-foreground">Processing</h3>
                </div>
                <div className="space-y-3">
                  {activeJobs.map((job: any) => (
                    <JobCard key={job.id} job={job} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Recent Analyses */}
          <div className="lg:col-span-2 animate-slideUp" style={{ animationDelay: '0.3s' }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-display font-semibold text-foreground">Recent Analyses</h2>
              <Link
                to="/analyses"
                className="text-sm text-gold-400 hover:text-gold-300 flex items-center gap-1 transition-colors group"
              >
                View all
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {analysesLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="skeleton h-24 rounded-xl" />
                ))}
              </div>
            ) : recentAnalyses.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="space-y-4">
                {recentAnalyses.map((analysis: any, index: number) => (
                  <AnalysisCard
                    key={analysis.id}
                    analysis={analysis}
                    style={{ animationDelay: `${0.3 + index * 0.05}s` }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="px-8 py-6 border-t border-border/30">
        <div className="flex items-center justify-between">
          <PoweredByTao />
          <p className="text-xs text-muted-foreground/50">
            {new Date().getFullYear()} TAO Solutions. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

// Quick Action Card
interface QuickActionCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  to: string;
  isPrimary?: boolean;
}

function QuickActionCard({ title, description, icon, to, isPrimary }: QuickActionCardProps) {
  return (
    <Link
      to={to}
      className={cn(
        'block p-5 rounded-xl border transition-all duration-300 group',
        isPrimary
          ? 'glass-elevated border-gold-500/30 hover:border-gold-500/50'
          : 'glass border-border/50 hover:border-gold-500/30'
      )}
    >
      <div className="flex items-center gap-4">
        <div
          className={cn(
            'p-3 rounded-xl transition-all duration-300',
            isPrimary
              ? 'bg-gold-500/20 text-gold-400 group-hover:bg-gold-500 group-hover:text-primary-950 group-hover:shadow-glow-gold'
              : 'bg-muted text-muted-foreground group-hover:bg-gold-500/10 group-hover:text-gold-400'
          )}
        >
          {icon}
        </div>
        <div>
          <p className={cn('font-semibold', isPrimary ? 'text-gold-400' : 'text-foreground')}>
            {title}
          </p>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <ArrowRight
          className={cn(
            'w-5 h-5 ml-auto opacity-0 -translate-x-2 transition-all group-hover:opacity-100 group-hover:translate-x-0',
            isPrimary ? 'text-gold-400' : 'text-muted-foreground'
          )}
        />
      </div>
    </Link>
  );
}

// Analysis Card
function AnalysisCard({ analysis, style }: { analysis: any; style?: React.CSSProperties }) {
  const statusConfig: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
    draft: { label: 'Draft', className: 'badge-muted', icon: <FileText className="w-3 h-3" /> },
    in_progress: { label: 'In Progress', className: 'badge-warning', icon: <Clock className="w-3 h-3" /> },
    completed: { label: 'Completed', className: 'badge-success', icon: <CheckCircle className="w-3 h-3" /> },
    archived: { label: 'Archived', className: 'badge-muted', icon: <FileText className="w-3 h-3" /> },
  };

  const status = statusConfig[analysis.status] || statusConfig.draft;

  return (
    <Link
      to="/analyses/$id"
      params={{ id: analysis.id }}
      className="block glass rounded-xl p-5 hover:glass-elevated transition-all duration-300 group animate-slideUp"
      style={style}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gold-500/10 flex items-center justify-center text-gold-400 group-hover:bg-gold-500/20 transition-colors">
            <FileText className="w-6 h-6" />
          </div>
          <div>
            <p className="font-semibold text-foreground group-hover:text-gold-400 transition-colors">
              {analysis.businessName || 'Untitled Analysis'}
            </p>
            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(analysis.createdAt)}
              </span>
              <span className="text-border">&bull;</span>
              <span>{analysis.reportId}</span>
            </div>
          </div>
        </div>
        <div className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium', status.className)}>
          {status.icon}
          {status.label}
        </div>
      </div>
    </Link>
  );
}

// Job Card
function JobCard({ job }: { job: any }) {
  return (
    <div className="glass rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className="spinner-gold w-5 h-5" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-foreground capitalize truncate">
            {job.jobType.replace(/-/g, ' ')}
          </p>
          <p className="text-xs text-muted-foreground">
            Attempt {job.attempts}/{job.maxAttempts || 3}
          </p>
        </div>
      </div>
    </div>
  );
}

// Empty State
function EmptyState() {
  return (
    <div className="glass-elevated rounded-2xl p-12 text-center">
      <div className="inline-flex mb-6">
        <div className="relative">
          <div className="w-20 h-20 rounded-2xl bg-gold-500/10 flex items-center justify-center">
            <FileText className="w-10 h-10 text-gold-400" />
          </div>
          <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-gold-500 flex items-center justify-center shadow-glow-gold">
            <Sparkles className="w-4 h-4 text-primary-950" />
          </div>
        </div>
      </div>

      <h3 className="text-xl font-display font-semibold text-foreground mb-2">No analyses yet</h3>
      <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
        Start your first credit analysis to see it appear here. Our AI will help you extract and analyze financial data.
      </p>

      <Link to="/workflow">
        <Button variant="gold" size="lg" className="gap-2">
          <Zap className="w-5 h-5" />
          Create Your First Analysis
        </Button>
      </Link>
    </div>
  );
}
